import * as React from 'react';

import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';

export const LEARNER_LIST_PAGE_SIZE = 7;

export type LearnerRow = {
  learner: Learner;
  /**
   * True when the learner is part of the current cohort. Drives the
   * trailing-cell affordance: trash icon (in list) vs + Add (not in list,
   * only possible in editor mode where `searchPool` brings outsiders in).
   */
  inList: boolean;
};

type Params = {
  /** The current cohort: what's rendered when the search query is empty. */
  list: Learner[];
  /**
   * Pool of learners outside the cohort the admin can add. Must be
   * disjoint from `list` — the caller is expected to subtract the cohort
   * from any wider org pool before passing it in. The hook treats every
   * row from this pool as `inList: false` without doing its own dedup.
   * Omitted by the viewer, which only ever searches the cohort.
   */
  availablePool?: Learner[];
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  /** Total number of rows matched by the current query. */
  filteredCount: number;
};

const normalise = (value: string) => value.trim().toLowerCase();

const matchesQuery = (learner: Learner, query: string): boolean => {
  if (query === '') return true;
  return learner.name.toLowerCase().includes(query) || learner.email.toLowerCase().includes(query);
};

/**
 * Client-side search + pagination for the Learner list and Add or remove
 * learners screens. Pure synchronous derivation — both the cohort and the
 * available pool are passed in already loaded.
 *
 * In editor mode (`availablePool` provided), the two lists model the
 * "cohort" and "everyone else the admin can add" as disjoint sets. When a
 * search query is non-empty, matches from the cohort render first (with
 * trash icons), followed by matches from the available pool (with +Add).
 */
const useLearnerList = ({ list, availablePool }: Params): Result => {
  const [searchQuery, setSearchQueryRaw] = React.useState('');
  const [page, setPage] = React.useState(1);

  const filtered: LearnerRow[] = React.useMemo(() => {
    const query = normalise(searchQuery);
    // No query → render the cohort only, in its given order. The available
    // pool is only surfaced when the admin is actively searching for
    // someone to add.
    if (query === '') return list.map((learner) => ({ learner, inList: true }));

    const inListMatches: LearnerRow[] = list
      .filter((l) => matchesQuery(l, query))
      .map((learner) => ({ learner, inList: true }));

    if (!availablePool) return inListMatches;

    const outListMatches: LearnerRow[] = availablePool
      .filter((l) => matchesQuery(l, query))
      .map((learner) => ({ learner, inList: false }));

    return [...inListMatches, ...outListMatches];
  }, [list, availablePool, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / LEARNER_LIST_PAGE_SIZE));
  // Clamp so the page stays valid when the filter shrinks the result set.
  const safePage = Math.min(Math.max(1, page), pageCount);

  const pageRows: LearnerRow[] = React.useMemo(
    () => filtered.slice((safePage - 1) * LEARNER_LIST_PAGE_SIZE, safePage * LEARNER_LIST_PAGE_SIZE),
    [filtered, safePage]
  );

  // Reset to page 1 whenever the search query changes so the user always
  // sees the most relevant results first.
  const setSearchQuery = React.useCallback((next: string) => {
    setSearchQueryRaw(next);
    setPage(1);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    page: safePage,
    setPage,
    pageCount,
    pageRows,
    filteredCount: filtered.length,
  };
};

export default useLearnerList;
